import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Copy, Check, Loader2, Facebook, Instagram, Linkedin, MapPin, Star, FileText,
  ChevronDown, RefreshCw, CheckCircle2, Circle, ExternalLink,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2', maxChars: 500 },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C', maxChars: 300 },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2', maxChars: 500 },
  { id: 'google', label: 'Google Business', icon: Star, color: '#EA4335', maxChars: 300 },
  { id: 'nextdoor', label: 'Nextdoor', icon: MapPin, color: '#8ED500', maxChars: 300 },
];

export default function PublishTab() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [adapting, setAdapting] = useState({});
  const [adapted, setAdapted] = useState({});
  const [copied, setCopied] = useState({});

  const fetchApproved = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/content`, { headers: authHeaders() });
      const approved = (res.data.content || []).filter(c => c.status === 'approved');
      setContent(approved);
    } catch (e) { /* fetch failed */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApproved(); }, [fetchApproved]);

  const handleAdapt = async (contentId, platform) => {
    const key = `${contentId}_${platform}`;
    setAdapting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/content/${contentId}/adapt`, { platform }, { headers: authHeaders() });
      setAdapted(prev => ({ ...prev, [key]: res.data }));
    } catch (e) { /* adapt failed */ }
    finally { setAdapting(prev => ({ ...prev, [key]: false })); }
  };

  const handleCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    } catch (e) { /* copy failed */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="publish-loading">
        <Loader2 size={24} className="animate-spin" style={{ color: '#8C7E6E' }} />
      </div>
    );
  }

  if (content.length === 0) {
    return (
      <div className="text-center py-16" data-testid="publish-empty">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1D6B4F10' }}>
          <Send size={28} strokeWidth={1} style={{ color: '#1D6B4F' }} />
        </div>
        <h3 className="text-base font-medium mb-2" style={{ color: '#3C3226' }}>No approved content to publish</h3>
        <p className="text-[13px] max-w-xs mx-auto" style={{ color: '#8C7E6E' }}>
          Approve content from the Content tab first. Then come here to adapt and publish it across platforms.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="publish-tab">
      <p className="text-[12px] mb-6" style={{ color: '#8C7E6E' }}>
        Select approved content, adapt it for each platform with AI, then copy and paste to publish.
      </p>

      <div className="space-y-3">
        {content.map((item, i) => {
          const isExpanded = expandedId === item.content_id;
          const platforms = item.platforms || {};

          return (
            <motion.div
              key={item.content_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              style={{ border: '1px solid #DDD5C8', backgroundColor: '#F7F4EF' }}
              data-testid={`publish-card-${i}`}
            >
              {/* Header */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : item.content_id)}
                className="w-full flex items-center gap-3 p-4 text-left"
                data-testid={`publish-toggle-${i}`}
              >
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1D6B4F10' }}>
                  <FileText size={16} style={{ color: '#1D6B4F' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate" style={{ color: '#3C3226' }}>{item.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-medium uppercase" style={{ color: '#1D6B4F' }}>Approved</span>
                    <PublishProgress platforms={platforms} />
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  className="flex-shrink-0 transition-transform"
                  style={{ color: '#8C7E6E', transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                />
              </button>

              {/* Expanded: platform adaptation cards */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4" style={{ borderTop: '1px solid #DDD5C8' }}>
                      {/* Original preview */}
                      <div className="mt-3 mb-4 p-3" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}>
                        <p className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: '#B8985A' }}>Original</p>
                        <p className="text-[12px] leading-relaxed line-clamp-4" style={{ color: '#5C5347' }}>{item.body}</p>
                      </div>

                      {/* Platform cards */}
                      <div className="space-y-2">
                        {PLATFORMS.map(plat => {
                          const key = `${item.content_id}_${plat.id}`;
                          const isAdapting = adapting[key];
                          const adaptData = adapted[key];
                          const isCopied = copied[key];
                          const isPosted = platforms[plat.id]?.posted || platforms[plat.id]?.user_confirmed;
                          const Icon = plat.icon;

                          return (
                            <div
                              key={plat.id}
                              className="p-3"
                              style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}
                              data-testid={`platform-adapt-${plat.id}-${i}`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 flex items-center justify-center" style={{ backgroundColor: `${plat.color}10` }}>
                                  <Icon size={14} style={{ color: plat.color }} />
                                </div>
                                <span className="text-[12px] font-medium" style={{ color: '#3C3226' }}>{plat.label}</span>
                                <span className="text-[10px]" style={{ color: '#8C7E6E' }}>max {plat.maxChars} chars</span>
                                {isPosted && (
                                  <span className="ml-auto flex items-center gap-1 text-[10px] font-medium" style={{ color: '#1D6B4F' }}>
                                    <CheckCircle2 size={12} /> Published
                                  </span>
                                )}
                              </div>

                              {adaptData ? (
                                <div>
                                  <p className="text-[12px] leading-relaxed mb-2" style={{ color: '#3C3226' }}>{adaptData.adapted_text}</p>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{adaptData.char_count}/{adaptData.max_chars}</span>
                                    <button
                                      onClick={() => handleCopy(key, adaptData.adapted_text)}
                                      className="ml-auto flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium transition-all"
                                      style={{
                                        backgroundColor: isCopied ? '#1D6B4F' : '#2C2520',
                                        color: '#F0EBE3',
                                      }}
                                      data-testid={`copy-adapted-${plat.id}-${i}`}
                                    >
                                      {isCopied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                    </button>
                                    <button
                                      onClick={() => handleAdapt(item.content_id, plat.id)}
                                      className="flex items-center gap-1 px-2 py-1.5 text-[11px]"
                                      style={{ border: '1px solid #DDD5C8', color: '#5C5347' }}
                                      data-testid={`re-adapt-${plat.id}-${i}`}
                                    >
                                      <RefreshCw size={11} /> Redo
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleAdapt(item.content_id, plat.id)}
                                  disabled={isAdapting}
                                  className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium transition-all"
                                  style={{
                                    backgroundColor: '#F7F4EF',
                                    border: '1px solid #DDD5C8',
                                    color: '#3C3226',
                                    opacity: isAdapting ? 0.6 : 1,
                                  }}
                                  data-testid={`adapt-btn-${plat.id}-${i}`}
                                >
                                  {isAdapting ? <><Loader2 size={12} className="animate-spin" /> Adapting...</> : <>Adapt for {plat.label}</>}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function PublishProgress({ platforms }) {
  const total = PLATFORMS.length;
  const posted = PLATFORMS.filter(p => platforms[p.id]?.posted || platforms[p.id]?.user_confirmed).length;
  if (posted === 0) return null;
  return (
    <span className="text-[10px]" style={{ color: '#8C7E6E' }}>
      {posted}/{total} platforms
    </span>
  );
}
